/* eslint-disable max-lines */
/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { AgGridAngular } from '@ag-grid-community/angular';
import {
  CellClickedEvent,
  ColDef,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
  IRowNode,
  RowNode,
} from '@ag-grid-community/core';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { AsyncPipe, NgClass, NgStyle, SlicePipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Injector,
  runInInjectionContext,
  Signal,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  NgMultiLabelTemplateDirective,
  NgOptionTemplateDirective,
  NgSelectComponent,
} from '@ng-select/ng-select';
import dayjs, { Dayjs } from 'dayjs';
import { isNull, isNumber } from 'lodash-es';
import { BehaviorSubject, combineLatest, firstValueFrom, from, of, ReplaySubject } from 'rxjs';
import { map, startWith, switchMap, take } from 'rxjs/operators';

import { AddPoReportUploadComponent } from '@widgets/add-po-report-upload/add-po-report-upload.component';

import { ExportExcelButtonComponent } from '@features/export-excel-button/export-excel-button.component';
import { PurchaseOrderWorkflowDirective } from '@features/feature-workflow/directives/purchase-order-workflow.directive';
import { VendorWorkflowDirective } from '@features/feature-workflow/directives/vendor-workflow.directive';

import { AgActionsComponent } from '@shared/ag-components/ag-actions/ag-actions.component';
import {
  AgDotsComponent,
  AgDotsParamsOption,
} from '@shared/ag-components/ag-dots/ag-dots.component';
import { AgIconLinkCellComponentParams } from '@shared/ag-components/ag-icon-link-cell/ag-icon-link-cell.model';
import { ButtonComponent } from '@shared/components/button/button.component';
import {
  AuxButtonGroup,
  ButtonGroupComponent,
} from '@shared/components/button-group/button-group.component';
import { CheckboxComponent } from '@shared/components/checkbox/checkbox.component';
import { ErrorsTooltipComponent } from '@shared/components/errors-tooltip/errors-tooltip.component';
import { ConfirmationModalComponent } from '@shared/components/modals/confirmation-modal/confirmation-modal.component';
import { MultiSelectDropdownComponent } from '@shared/components/multi-select-dropdown/multi-select-dropdown.component';
import { TablePanelComponent } from '@shared/components/table-panel/table-panel.components';
import { TableSkeletonComponent } from '@shared/components/table-skeleton/table-skeleton.component';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { ROUTING_PATH } from '@shared/constants/routingPath';
import { TableConstants } from '@shared/constants/table.constants';
import { StickyGridDirective } from '@shared/directives/sticky-grid/sticky-grid.directive';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { MoneyPipe } from '@shared/pipes/money.pipe';
import { ApiService } from '@shared/services/api.service';
import {
  Currency,
  DocumentType,
  EntityType,
  EventType,
  GqlService,
  InvoiceStatus,
  InvoiceSummary,
  listBudgetTotalsByPurchaseOrderQuery,
  listDocumentsQuery,
  listPermissionsQuery,
  listUserNamesWithEmailQuery,
  NotificationPage,
  PermissionType,
  TrialImplementationStatus,
  User,
  WorkflowStep,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { AuthQuery } from '@shared/store/auth/auth.query';
import { AuthService } from '@shared/store/auth/auth.service';
import { EventService } from '@shared/store/event/event.service';
import { GlobalStore } from '@shared/store/global/global.store';
import { InvoiceService } from '@shared/store/invoice/invoice.service';
import { MainQuery } from '@shared/store/main/main.query';
import { OrganizationQuery } from '@shared/store/organization/organization.query';
import { OrganizationService } from '@shared/store/organization/organization.service';
import {
  OrganizationModel,
  OrganizationStore,
} from '@shared/store/organization/organization.store';
import { PurchaseOrdersQuery } from '@shared/store/purchase-orders/purchase-orders.query';
import { PurchaseOrdersService } from '@shared/store/purchase-orders/purchase-orders.service';
import { sortByUserName, TrialUserQuery } from '@shared/store/trial-users/trial-user.query';
import { WorkflowQuery } from '@shared/store/workflow/workflow.query';
import { WorkflowService } from '@shared/store/workflow/workflow.service';
import { Option } from '@shared/types/components.type';
import { AuxExcelStyleKeys } from '@shared/utils';
import { injectExecutableByRoute } from '@shared/utils/route.utils';
import { ExportType, Utils } from '@shared/utils/utils';

import { BulkPoDialogComponent } from './bulk-upload-po-dialog/bulk-upload-po-dialog.component';
import { NewPoDialogComponent, NewPoDialogData } from './new-po-dialog/new-po-dialog.component';
import { PurchaseOrdersColumns } from './purchase-orders.constants';
import { POGridData } from './purchase-orders.model';
import {
  OrganizationDialogComponent,
  OrganizationDialogData,
} from '../vendors/organization-dialog/organization-dialog.component';
import {
  PoCoRequestModalComponent,
  RequestChangeOrderData,
} from './po-co-request-modal/po-co-request-modal/po-co-request-modal.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [PurchaseOrderWorkflowDirective],
  imports: [
    TableSkeletonComponent,
    TablePanelComponent,
    FormsModule,
    ReactiveFormsModule,
    NgSelectComponent,
    NgOptionTemplateDirective,
    TooltipDirective,
    NgMultiLabelTemplateDirective,
    MultiSelectDropdownComponent,
    CheckboxComponent,
    ButtonGroupComponent,
    ErrorsTooltipComponent,
    ButtonComponent,
    ExportExcelButtonComponent,
    AgGridAngular,
    StickyGridDirective,
    NgClass,
    NgStyle,
    MoneyPipe,
    AsyncPipe,
    SlicePipe,
  ],
  providers: [VendorWorkflowDirective],
  selector: 'aux-purchase-orders',
  styleUrl: 'purchase-orders.component.scss',
  templateUrl: './purchase-orders.component.html',
})
export class PurchaseOrdersComponent implements AfterViewInit {
  apiService = inject(ApiService);
  authQuery = inject(AuthQuery);
  authService = inject(AuthService);
  destroyRef = inject(DestroyRef);
  eventService = inject(EventService);
  formBuilder = inject(FormBuilder);
  globalStore = inject(GlobalStore);
  injector = inject(Injector);
  invoiceService = inject(InvoiceService);
  mainQuery = inject(MainQuery);
  organizationQuery = inject(OrganizationQuery);
  organizationService = inject(OrganizationService);
  organizationStore = inject(OrganizationStore);
  overlayService = inject(OverlayService);
  readonly purchaseOrderCtx = inject(PurchaseOrderWorkflowDirective);
  public purchaseOrdersQuery = inject(PurchaseOrdersQuery);
  purchaseOrdersService = inject(PurchaseOrdersService);
  route = inject(ActivatedRoute);
  router = inject(Router);
  vendorWorkflowDirective = inject(VendorWorkflowDirective);
  workflowQuery = inject(WorkflowQuery);
  workflowService = inject(WorkflowService);

  private gqlService = inject(GqlService);
  private trialUserQuery = inject(TrialUserQuery);

  POForm = this.formBuilder.group({
    invoices_through: new FormControl<null | string>(null),
    pos_with_variance: new FormControl<boolean>(false),
    purchase_order: new FormControl<null | string[]>(null),
    selected_vendors: new FormControl<null | string[]>(null),
  });

  //FORM
  poFormAsSignal = toSignal(this.POForm.valueChanges.pipe(startWith(this.POForm.value)), {
    injector: this.injector,
  });
  //VENDOR DROPDOWN SIGNALS
  signalVendors = toSignal(this.organizationQuery.allVendors$, { injector: this.injector });

  filteredVendorOptions = computed(() => {
    return (this.signalVendors() || []).filter((vendor) => {
      const form_value = this.poFormAsSignal();

      if (form_value && form_value.purchase_order && form_value.purchase_order.length) {
        const filteredGrid =
          this.gridDataAsSignal()?.filter(
            (po) => po.po_id && form_value.purchase_order?.includes(po.po_id)
          ) || [];

        return filteredGrid.some((po) => po.organization_id == vendor.id);
      } else {
        return true;
      }
    });
  });
  lastPOReportUpdatedText = signal(Utils.zeroHyphen);

  gridData$ = new BehaviorSubject<POGridData[]>([]);

  //GRID DATA
  gridDataAsSignal = toSignal(this.gridData$, { injector: this.injector });
  trialInformation = toSignal(
    this.gqlService.getTrialInformation$().pipe(
      map(({ data: trials }) => {
        return trials;
      })
    ),
    { injector: this.injector }
  );
  //Users With Update Change Order Budget Permission for request change order
  trialUsers = toSignal(
    this.trialUserQuery.selectAll({
      sortBy: sortByUserName,
    }),
    { injector: this.injector }
  );
  updateCOPerm = toSignal(
    this.gqlService.listPermissions$().pipe(
      map(({ data }) => {
        const perm = data?.find(
          (perm) => perm.permission_type === PermissionType.PERMISSION_UPDATE_CHANGE_ORDER
        );

        return perm ? perm : ({} as listPermissionsQuery);
      })
    ),
    { injector: this.injector }
  );

  hasBulkUploadPOsPermission = this.authService.$isAuthorized({
    permissions: [PermissionType.PERMISSION_BULK_UPLOAD_PURCHASE_ORDERS],
  });
  hasUpdatePOsPermission = this.authService.$isAuthorized({
    permissions: [PermissionType.PERMISSION_UPDATE_PURCHASE_ORDERS],
  });

  private onAddBulkUploadPoClick = async (type: 'UPDATE' | 'UPLOAD'): Promise<void> => {
    const action = async (instance: BulkPoDialogComponent | null) => {
      await instance?.onSave();
      await this.updateFiles();
    };

    const ref = this.overlayService.openPopup({
      content: BulkPoDialogComponent,
      data: { type },
      settings: {
        header: type === 'UPLOAD' ? 'Bulk Upload POs' : 'Bulk Update POs',
        primaryButton: {
          action,
          disabled: (instance: BulkPoDialogComponent | null) => !!instance?.isSaveButtonDisabled(),
          label: 'Upload',
          pendoTag: type === 'UPLOAD' ? 'bulk-upload-po-button' : 'bulk-update-po-button',
          tooltip: (instance: BulkPoDialogComponent | null) =>
            instance ? instance.saveButtonTooltip() : '',
        },
      },
    });

    const resp = await firstValueFrom(ref.afterClosed$);

    if (resp.data) {
      await this.updateFiles();
    }
  };

  buttonGroupOptions: Signal<AuxButtonGroup[]> = computed<AuxButtonGroup[]>(() => [
    {
      name: 'Add new purchase order',
      onClick: () => {
        this.onAddPO().catch(console.error);
      },
      pendoId: 'add-new-po',
    },
    {
      disabled: !this.hasBulkUploadPOsPermission() || !this.hasUpdatePOsPermission(),
      disabledTooltip:
        !this.hasBulkUploadPOsPermission() || !this.hasUpdatePOsPermission()
          ? MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION
          : '',
      name: 'Bulk Update POs',
      onClick: () => this.onAddBulkUploadPoClick('UPDATE'),
      pendoId: 'bulk-update-po',
    },
    {
      disabled: !this.hasBulkUploadPOsPermission() || !this.hasUpdatePOsPermission(),
      disabledTooltip:
        !this.hasBulkUploadPOsPermission() || !this.hasUpdatePOsPermission()
          ? MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION
          : '',
      name: 'Bulk Upload POs',
      onClick: () => this.onAddBulkUploadPoClick('UPLOAD'),
      pendoId: 'bulk-upload-po',
    },
  ]);
  filteredGridData = computed<POGridData[]>(() => {
    const listData = this.gridDataAsSignal();
    const form_value = this.poFormAsSignal();

    if (listData && form_value) {
      const newGridData = listData
        .filter(
          (po) =>
            this.filterPONumber(po, form_value.purchase_order) &&
            this.filterVariance(po, form_value.pos_with_variance) &&
            this.filterVendor(po, form_value.selected_vendors)
        )
        .map((po) => {
          return this.filterInvoicesThrough(po, form_value.invoices_through);
        });

      return newGridData;
    }

    return [] as POGridData[];
  });
  // CURRENCIES
  currencies = computed<Set<Currency>>(() => {
    const gridData = this.filteredGridData();
    const currencies = new Set<Currency>();

    gridData.forEach((po) => currencies.add(po.currency));

    return currencies;
  });
  dateOptions = computed(() => {
    const trials = this.trialInformation();
    let maxDate = '2000-01-01';

    this.gridDataAsSignal()?.forEach((po) => {
      if (po.invoice_summaries && po.invoice_summaries.length) {
        po.invoice_summaries.forEach((is) => {
          if (is.accrual_period && dayjs(is.accrual_period).isAfter(maxDate)) {
            maxDate = is.accrual_period;
          }
        });
      }
    });

    return trials?.[0]
      ? this.purchaseOrdersService.getInvoicesThroughOptions(
          trials[0].trial_start_date,
          dayjs(trials[0].trial_end_date).isBefore(dayjs(maxDate))
            ? dayjs(maxDate).add(1, 'month').format('YYYY-MM-01')
            : trials[0].trial_end_date
        )
      : [];
  });
  //PO IDS
  filteredPOIDs = computed(() => {
    return this.filteredGridData().map((po) => {
      return po.po_id;
    });
  });
  //PO NUMBER DROWDOWN SIGNALS
  PONumbers = computed(() => {
    return this.purchaseOrdersService.$PONumbers();
  });
  filteredPONumbers = computed(() => {
    return this.PONumbers().filter((poNum) => {
      const form_value = this.poFormAsSignal();

      if (form_value && form_value.selected_vendors && form_value.selected_vendors.length) {
        const filteredGrid =
          this.gridDataAsSignal()?.filter(
            (po) => po.organization_id && form_value.selected_vendors?.includes(po.organization_id)
          ) || [];

        return filteredGrid.some((po) => po.po_id == poNum.id);
      } else {
        return true;
      }
    });
  });
  getSelectedCurrency = computed<Currency | undefined>(() => {
    return this.currencies().size === 1 ? this.currencies().values().next().value : undefined;
  });
  requestCOUsers = computed(() => {
    const allUsers = this.trialUsers();
    const perm = this.updateCOPerm();

    if (allUsers && perm) {
      return allUsers
        .filter((user) => user.permissions[perm.id] === 'E')
        .map((user) => {
          return {
            label: `${user.given_name} ${user.family_name}, ${user.email}`,
            value: user.email,
          } as Option;
        });
    } else {
      return [] as Option[];
    }
  });

  addingVendor$ = new BehaviorSubject(false);
  lastPOReportUpdating$ = new BehaviorSubject(false);

  loading$ = combineLatest([
    this.purchaseOrdersQuery.selectLoading(),
    this.addingVendor$,
    this.lastPOReportUpdating$,
  ]).pipe(
    map(([purchaseOrders, addingVendor, lastPOReportUpdating]) => {
      if (!(purchaseOrders || addingVendor || lastPOReportUpdating)) {
        this.initializeActionsColumn();
      }

      return purchaseOrders || addingVendor || lastPOReportUpdating;
    })
  );

  buttonGroupOverlayPosition: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
    },
  ];
  //INVOICES THROUGH DROPDOWN
  components = {
    agDotsComponent: AgDotsComponent,
  };
  files$ = new BehaviorSubject<listDocumentsQuery['items']>([]);
  gridAPI!: GridApi;
  gridAPI$ = new ReplaySubject<GridApi>(1);
  gridOptions$ = new BehaviorSubject<GridOptions>({
    ...TableConstants.DEFAULT_GRID_OPTIONS.GRID_OPTIONS,
    ...TableConstants.DEFAULT_GRID_OPTIONS.DEFAULT_COL_DEF,
    autoGroupColumnDef: {
      cellRendererParams: {
        suppressCount: true,
      },
      headerName: '',
      maxWidth: 200,
      minWidth: 100,
    },
    columnDefs: [],
    getRowStyle: (param) => {
      if (param.node.rowPinned) {
        return { 'font-weight': 'bold' };
      }

      return undefined;
    },
    rowHeight: 40,
    rowSelection: 'single',
    suppressMenuHide: true,
  });
  hasBudgetEditPermission$ = new BehaviorSubject<boolean>(false);
  hasPOPermission$ = new BehaviorSubject<boolean>(false);
  hasUploadReportPermission = this.authService.$isAuthorized({
    permissions: [PermissionType.PERMISSION_UPLOAD_PURCHASE_ORDER_REPORT],
  });
  isChangeOrdersWorkflowLocked = this.workflowQuery.getLockStatusByWorkflowStepType(
    WorkflowStep.WF_STEP_MONTH_CLOSE_LOCK_CHANGE_ORDERS
  );
  isWarningsVisible$ = new BehaviorSubject<boolean>(false);
  listBudgetTotalsByPurchaseOrder$ = new BehaviorSubject<listBudgetTotalsByPurchaseOrderQuery[]>(
    []
  );
  optionTooltipPositions: ConnectedPosition[] = [
    {
      offsetX: -10,
      offsetY: 8,
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
    },
  ];
  users = new Map<string, Pick<User, 'email' | 'family_name' | 'given_name'>>();

  ngAfterViewInit(): void {
    runInInjectionContext(this.injector, () => {
      injectExecutableByRoute(
        ({ modalVendorId }) => {
          this.onAddPO(modalVendorId).catch(console.error);
        },
        this.purchaseOrderCtx.disabledAddPoUI,
        'modalVendorId'
      );
    });

    runInInjectionContext(this.injector, () => {
      injectExecutableByRoute(() => {
        this.onAddPO().catch(console.error);
      }, this.purchaseOrderCtx.disabledAddPoUI);
    });
  }

  attributesModalNavigate = (params: AgIconLinkCellComponentParams): void => {
    this.router
      .navigate([ROUTING_PATH.BUDGET.INDEX, ROUTING_PATH.BUDGET.BUDGET.INDEX], {
        queryParams: {
          openModalAttributes: !!params.data?.organization_id,
          vendorId: params.data?.organization_id || null,
        },
      })
      .catch(console.error);
  };

  budgetNavigate = (params: AgIconLinkCellComponentParams): void => {
    this.router
      .navigate([ROUTING_PATH.BUDGET.INDEX, ROUTING_PATH.BUDGET.BUDGET.INDEX], {
        queryParams: { vendorId: params.data?.organization_id || null },
      })
      .catch(console.error);
  };

  castItemsToVendorOptions(items: unknown): OrganizationModel[] {
    return Array.isArray(items) ? items : [];
  }

  constructor() {
    this.hasPermission();

    this.purchaseOrdersService.get().pipe(takeUntilDestroyed()).subscribe();

    this.purchaseOrdersService.getPONumbers().pipe(takeUntilDestroyed()).subscribe();

    this.getListBudgetTotalsByPurchaseOrder();

    this.mainQuery
      .select('trialKey')
      .pipe(
        switchMap(() => {
          return from(
            this.apiService.getFilesByFilters(
              this.getTrialPOPath(),
              undefined,
              EntityType.PURCHASE_ORDER,
              DocumentType.DOCUMENT_PURCHASE_ORDER
            )
          );
        }),
        takeUntilDestroyed()
      )
      .subscribe((files) => {
        this.files$.next(files);
      });

    combineLatest([
      this.purchaseOrdersQuery.selectSortedByPONumber$,
      this.files$,
      this.gridAPI$.pipe(take(1)),
      this.listBudgetTotalsByPurchaseOrder$,
      toObservable(this.globalStore.primaryAttribute),
      toObservable(this.globalStore.secondaryAttribute),
    ])
      .pipe(takeUntilDestroyed())
      .subscribe(([POs, files]) => {
        this.gridData$.next(
          POs.map(
            ({
              currency,
              description,
              id,
              invoice_summaries,
              organization_id,
              organization_name,
              paid_amount,
              po_number,
              po_report_amount,
              po_report_invoiced_amount,
              po_report_status,
              po_status,
              received_amount,
            }) => ({
              currency,
              description,
              file: files.findIndex((file) => file.bucket_key.includes(id)) > -1,
              invoice_summaries,
              invoiced_against:
                isNumber(paid_amount) || isNumber(received_amount)
                  ? Utils.roundToNumber((paid_amount || 0) + (received_amount || 0), 2)
                  : null,
              invoiced_against_variance: [
                paid_amount,
                received_amount,
                po_report_invoiced_amount,
              ].some((item) => isNumber(item))
                ? Utils.roundToNumber(
                    (paid_amount || 0) + (received_amount || 0) - (po_report_invoiced_amount || 0),
                    2
                  )
                : null,
              isPoEditableByPivot: this.isPoEditableByPivot(id),
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              last_invoice_date: this.getLastInvoiceDate(invoice_summaries || []),
              linkedActivityTotal: this.getLinkedActivityTotal(id),
              organization_id,
              organization_name,
              paid_amount,
              paid_percentage: this.getPercentage(id, paid_amount),
              po_amount_variance:
                isNumber(this.getLinkedActivityTotal(id)) || isNumber(po_report_amount)
                  ? Utils.roundToNumber(
                      (this.getLinkedActivityTotal(id) || 0) - (po_report_amount || 0),
                      2
                    )
                  : null,
              po_id: id,
              po_number,
              po_remaining: [this.getLinkedActivityTotal(id), paid_amount, received_amount].some(
                (item) => isNumber(item)
              )
                ? Utils.roundToNumber(
                    (this.getLinkedActivityTotal(id) || 0) -
                      (paid_amount || 0) -
                      (received_amount || 0),
                    2
                  )
                : null,
              po_remaining_invoiced_amount:
                isNumber(po_report_amount) || isNumber(po_report_invoiced_amount)
                  ? Utils.roundToNumber(
                      (po_report_amount || 0) - (po_report_invoiced_amount || 0),
                      2
                    )
                  : null,
              po_remaining_variance: [
                this.getLinkedActivityTotal(id),
                paid_amount,
                received_amount,
                po_report_amount,
                po_report_invoiced_amount,
              ].some((item) => isNumber(item))
                ? Utils.roundToNumber(
                    (this.getLinkedActivityTotal(id) || 0) -
                      (paid_amount || 0) -
                      (received_amount || 0) -
                      ((po_report_amount || 0) - (po_report_invoiced_amount || 0)),
                    2
                  )
                : null,
              po_report_amount,
              po_report_invoiced_amount,
              po_report_status: po_report_status,
              po_status: po_status,
              received_amount,
              received_percentage: this.getPercentage(id, received_amount),
            })
          )
        );
        this.refreshGridFilter(this.gridData$.getValue());
      });

    this.organizationService
      .get()
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        const vendors = this.organizationQuery.getAllVendors();

        if (vendors.length === 1) {
          this.organizationStore.setActive(vendors[0].id);
          this.POForm.controls.selected_vendors.setValue([vendors[0].id]);
        } else {
          // reset any older selected vendors.
          this.organizationStore.setActive(null);
          this.POForm.controls.selected_vendors.setValue([]);
        }
      });

    this.workflowService.getWorkflowList().pipe(takeUntilDestroyed()).subscribe();

    this.initUserList();

    this.setLastPOReportUpdated(true);

    this.updatePoListOnReportUpdated();

    this.updatePoListOnBulkUploaded();

    this.updatePoListOnBulkUpdateUploaded();

    effect(() => {
      if (this.filteredGridData()) {
        this.refreshGridFilter(this.filteredGridData());
      }
    });
  }

  filterInvoicesThrough(po: POGridData, invoice_through?: null | string): POGridData {
    if (invoice_through && po.invoice_summaries && po.invoice_summaries.length) {
      const filteredInvoices = po.invoice_summaries.filter((is) => {
        return dayjs(is.accrual_period).isSameOrBefore(dayjs(invoice_through), 'month');
      }) as InvoiceSummary[];

      const num = filteredInvoices.reduce((acc, value) => {
        if (value && value.total_amount) {
          return acc + value?.total_amount;
        }

        return acc;
      }, 0);

      const obj: {
        paid_amount: null | number;
        received_amount: null | number;
      } = {
        paid_amount: null,
        received_amount: null,
      };

      filteredInvoices.forEach((invoice_summary) => {
        switch (invoice_summary.invoice_status) {
          case InvoiceStatus.STATUS_APPROVED:
            if (isNumber(invoice_summary.total_amount)) {
              if (isNull(obj.paid_amount)) {
                obj.paid_amount = 0;
              }

              obj.paid_amount += invoice_summary.total_amount;
            }

            break;
          case InvoiceStatus.STATUS_IN_QUEUE:
          case InvoiceStatus.STATUS_PENDING_APPROVAL:
          case InvoiceStatus.STATUS_PENDING_REVIEW:
            if (isNumber(invoice_summary.total_amount)) {
              if (isNull(obj.received_amount)) {
                obj.received_amount = 0;
              }

              obj.received_amount += invoice_summary.total_amount;
            }

            break;
          default:
            break;
        }
      });

      const poRemaining = (this.getLinkedActivityTotal(po.po_id) || 0) - num;

      return {
        ...po,
        ...obj,
        invoiced_against: num,
        invoiced_against_variance: num - (po.po_report_invoiced_amount || 0),
        last_invoice_date: this.getLastInvoiceDate(filteredInvoices),
        paid_percentage: this.getPercentage(po.po_id, obj.paid_amount),
        po_remaining: poRemaining,
        po_remaining_variance: poRemaining - (po.po_remaining_invoiced_amount || 0),
        received_percentage: this.getPercentage(po.po_id, obj.received_amount),
      };
    } else {
      return po;
    }
  }

  filterPONumber(po: POGridData, po_ids?: null | string[]): boolean {
    return po_ids && po_ids.length ? po_ids.includes(po.po_id) : true;
  }

  filterVariance(po: POGridData, checked?: boolean | null): boolean {
    return checked
      ? !!po.po_amount_variance ||
          !!po.invoiced_against_variance ||
          !!po.po_remaining_variance ||
          po.po_status !== po.po_report_status
      : true;
  }

  filterVendor(po: POGridData, vendors?: null | string[]): boolean {
    return vendors && !!vendors.length
      ? !!po.organization_id && vendors.includes(po.organization_id)
      : true;
  }

  generatePinnedBottomData(rows: POGridData[]) {
    const fields = [
      'po_report_amount',
      'po_amount_variance',
      'invoiced_against',
      'po_report_invoiced_amount',
      'invoiced_against_variance',
      'po_remaining',
      'po_remaining_invoiced_amount',
      'po_remaining_variance',
      'linkedActivityTotal',
    ] as const;
    const data = rows.reduce(
      (acc, val) => {
        fields.forEach((field) => {
          const selectedVendors = this.POForm.controls.selected_vendors.value;

          if (
            !selectedVendors ||
            !selectedVendors.length ||
            (val.organization_id && selectedVendors.includes(val.organization_id))
          ) {
            if (isNumber(val[field])) {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              acc[field] += val[field];
            }
          }
        });

        return acc;
      },
      {
        invoiced_against: null,
        invoiced_against_variance: null,
        linkedActivityTotal: null,
        po_amount_variance: null,
        po_remaining: null,
        po_remaining_invoiced_amount: null,
        po_remaining_variance: null,
        po_report_amount: null,
        po_report_invoiced_amount: null,
      } as {
        [k: string]: null | number;
        invoiced_against: null | number;
        invoiced_against_variance: null | number;
        linkedActivityTotal: null | number;
        po_amount_variance: null | number;
        po_remaining: null | number;
        po_remaining_invoiced_amount: null | number;
        po_remaining_variance: null | number;
        po_report_amount: null | number;
        po_report_invoiced_amount: null | number;
      }
    );

    return [
      {
        ...data,
        actions: null,
        currency: this.getSelectedCurrency() || Currency.USD,
        organization_name: 'Total',
        po_number: '',
      },
    ];
  }

  getFilePath(poID: string) {
    const trialId = this.mainQuery.getValue().trialKey;

    return `trials/${trialId}/purchaseorders/${poID}/`;
  }

  private getLastInvoiceDate(invoiceSummaryList: InvoiceSummary[]): string {
    const dateList: Dayjs[] = [];

    invoiceSummaryList.forEach((summary) => {
      if (summary.invoice_date) {
        dateList.push(dayjs(summary.invoice_date));
      }
    });

    if (dateList.length > 0) {
      return dayjs.max(dateList)?.format('MM/DD/YYYY') || '';
    }

    return '';
  }

  private getLinkedActivityTotal(po_id: null | string): null | number {
    if (po_id) {
      const amount = this.listBudgetTotalsByPurchaseOrder$
        .getValue()
        .find((total) => total.po_id === po_id)?.amount;

      return isNumber(amount) ? amount : null;
    } else return null;
  }

  private getListBudgetTotalsByPurchaseOrder(): void {
    this.gqlService
      .listBudgetTotalsByPurchaseOrder$()
      .subscribe((result) => this.listBudgetTotalsByPurchaseOrder$.next(result.data || []));
  }

  private getPercentage(poId: null | string, amount: null | number): number {
    if (!this.getLinkedActivityTotal(poId) || !amount) {
      return 0;
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return Math.round((amount / this.getLinkedActivityTotal(poId)) * 10000) / 100;
  }

  getSelectedCurrencySymbol(): string | undefined {
    return Utils.getCurrenySymbol(this.getSelectedCurrency() || Currency.USD);
  }

  getTrialPOPath() {
    const trialId = this.mainQuery.getValue().trialKey;

    return `trials/${trialId}/purchaseorders/`;
  }

  gridActionButtonCreateChangeOrderTooltip = (params: ICellRendererParams): string[] => {
    return this.organizationQuery.getPrimaryBudgetVersion(params.data.organization_id)
      ? []
      : [MessagesConstants.CHANGE_ORDER_NOT_SUPPORTED_FOR_VENDOR_CONTRACT_WITHOUT_BUDGET];
  };

  gridActionButtonTooltip = (params?: ICellRendererParams): string[] => {
    const messages: string[] = [];

    if (!this.hasPOPermission$.getValue()) {
      messages.push(MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION);
    }

    if (this.isChangeOrdersWorkflowLocked()) {
      messages.push(MessagesConstants.LOCKED_FOR_PERIOD_CLOSE);
    }

    if (params && !params.data.isPoEditableByPivot) {
      messages.push(MessagesConstants.PO.PO_USED_FOR_CLOSED_CALCULATION);
    }

    return messages;
  };

  private hasPermission() {
    this.authService
      .isAuthorized$({
        permissions: [PermissionType.PERMISSION_UPDATE_PURCHASE_ORDERS],
        sysAdminsOnly: false,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((perm) => this.hasPOPermission$.next(perm));

    this.authService
      .isAuthorized$({
        permissions: [PermissionType.PERMISSION_EDIT_BUDGET],
        sysAdminsOnly: false,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((perm) => this.hasBudgetEditPermission$.next(perm));
  }

  initializeActionsColumn() {
    const actionColumn: ColDef[] = [];
    const leftActionColumn = {
      children: [
        {
          cellRenderer: AgActionsComponent,
          cellRendererParams: (params: ICellRendererParams) => ({
            disabled$: of(!params.data.file),
            downloadButtonClass: 'text-aux-gray-darkest',
            downloadClickFN:
              params.node.rowPinned === 'bottom'
                ? undefined
                : ({ rowNode }: { rowNode: RowNode }) => this.onDownloadLine(rowNode),
            tooltipText$: of(!params.data.file ? 'No supporting documents to download.' : ''),
          }),
          headerName: '',
          maxWidth: 40,
          minWidth: 40,
          resizable: false,
          sortable: false,
          suppressMenu: true,
        },
      ],
      field: 'action',
      headerName: '',
      maxWidth: 40,
      minWidth: 40,
      resizable: false,
      sortable: false,
      suppressMenu: true,
    };

    actionColumn.push({
      cellClass: [
        AuxExcelStyleKeys.BORDER_LEFT,
        TableConstants.STYLE_CLASSES.CELL_JUSTIFY_CENTER,
        TableConstants.STYLE_CLASSES.CELL_ALIGN_CENTER,
        '!p-0',
      ],
      cellRendererParams: (params: ICellRendererParams) => {
        const createChangeOrderTooltip = this.gridActionButtonCreateChangeOrderTooltip(params);

        return {
          options: <AgDotsParamsOption[]>[
            {
              disabled: this.gridActionButtonTooltip().length,
              onClick: async () => {
                await this.onUpdateLine(params.node);
              },
              optionClass: this.gridActionButtonTooltip().length ? 'opacity-50' : '',
              title: 'Edit',
              tooltip: this.gridActionButtonTooltip(),
            },
            {
              disabled: this.gridActionButtonTooltip(params).length,
              onClick: async () => {
                await this.onRemoveLine(params.node);
              },
              optionClass: this.gridActionButtonTooltip(params).length
                ? 'text-aux-red-dark opacity-50'
                : 'text-aux-red-dark',
              title: 'Delete',
              tooltip: this.gridActionButtonTooltip(params),
            },
            {
              disabled: !!createChangeOrderTooltip.length,
              onClick: () => {
                this.onCreateChangeOrder(params.node);
              },
              optionClass: createChangeOrderTooltip.length ? 'opacity-50' : '',
              title: 'Create Change Order',
              tooltip: this.gridActionButtonCreateChangeOrderTooltip(params),
            },
            {
              disabled: false,
              onClick: () => {
                this.onRequestChangeOrder(params.node).catch(console.error);
              },
              title: 'Request Change Order',
            },
            !params.data.organization_id
              ? {
                  disabled:
                    this.gridActionButtonTooltip().length ||
                    this.vendorWorkflowDirective.disabledUI(),
                  onClick: () => {
                    this.onAddVendor(params.node).catch(console.error);
                  },
                  optionClass:
                    this.gridActionButtonTooltip().length ||
                    this.vendorWorkflowDirective.disabledUI()
                      ? 'opacity-50'
                      : '',
                  title: 'Add Vendor Contract',
                  tooltip: this.gridActionButtonTooltip() || [
                    this.vendorWorkflowDirective.vendorActionTooltip(),
                  ],
                }
              : null,
          ],
        };
      },
      cellRendererSelector: (params) => {
        if (params.node?.isRowPinned()) {
          return {};
        }

        return { component: 'agDotsComponent', params };
      },
      field: 'action',
      headerClass: [AuxExcelStyleKeys.BORDER_LEFT],
      headerName: '',
      lockPosition: 'right',
      maxWidth: 40,
      minWidth: 40,
      pinned: 'right',
      resizable: false,
      sortable: false,
      suppressMenu: true,
    });

    const options = this.gridOptions$.getValue();

    options.columnDefs = [
      leftActionColumn,
      ...PurchaseOrdersColumns({
        attributesModalNavigate: this.attributesModalNavigate,
        budgetNavigate: this.budgetNavigate,
        hasBudgetEditPermission: this.hasBudgetEditPermission$.getValue(),
        invoicesNavigate: this.invoicesNavigate,
        isChangeOrdersWorkflowLocked: this.isChangeOrdersWorkflowLocked(),
        isWarningsVisible$: this.isWarningsVisible$,
      }),
      ...actionColumn,
    ];

    this.gridOptions$.next(options);
  }

  private initUserList(): void {
    this.mainQuery
      .select('userList')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((_users) => {
        _users.forEach((user: listUserNamesWithEmailQuery) => {
          this.users.set(user.sub, user);
        });
      });
  }

  invoicesNavigate = (event: CellClickedEvent): void => {
    this.invoiceService.setAccrualPeriodsAndVendorFilter(
      [],
      event.data?.organization_id ? [event.data?.organization_id] : []
    );
    this.router
      .navigate([
        ROUTING_PATH.CLOSING.INDEX,
        ROUTING_PATH.CLOSING.INVOICES_AND_PREPAIDS.INDEX,
        ROUTING_PATH.CLOSING.INVOICES_AND_PREPAIDS.INVOICES,
      ])
      .catch(console.error);
  };

  isPoEditableByPivot(poId: null | string): boolean {
    if (!poId) {
      return true;
    }

    const status = this.mainQuery.getSelectedTrial()?.implementation_status;
    const poLinked = this.listBudgetTotalsByPurchaseOrder$
      .getValue()
      .find((total) => total.po_id === poId);

    if (
      status !== TrialImplementationStatus.IMPLEMENTATION_STATUS_ARCHIVED &&
      status !== TrialImplementationStatus.IMPLEMENTATION_STATUS_LIVE
    ) {
      return true;
    }

    return !(this.isPoInPivot() && !!poLinked);
  }

  private isPoInPivot(): boolean {
    const pivotPrimaryAttributeName = this.globalStore.primaryAttribute.attribute_name();
    const pivotSecondaryAttributeName = this.globalStore.secondaryAttribute.attribute_name();

    return (
      (!!pivotPrimaryAttributeName && pivotPrimaryAttributeName === 'PO #') ||
      (!!pivotSecondaryAttributeName && pivotSecondaryAttributeName === 'PO #')
    );
  }

  async onAddPO(vendorId?: string) {
    const action = async (instance: NewPoDialogComponent | null) => {
      await instance?.onUpload();
      await this.updateFiles();
    };

    this.overlayService.openPopup<NewPoDialogData, unknown, NewPoDialogComponent>({
      content: NewPoDialogComponent,
      data: {
        mode: 'add',
        vendorId,
      },
      settings: {
        header: 'Add purchase order',
        primaryButton: {
          action,
          disabled: (instance) => !!instance?.isSaveButtonDisabled(),
          pendoTag: 'add-purchase-order-button',
          tooltip: (instance) => (instance ? instance?.saveButtonTooltip() : ''),
        },
      },
    });

    await this.updateFiles();
  }

  onAddPoReportUploadClick: () => void = async () => {
    const action = async (instance: AddPoReportUploadComponent | null) => {
      await instance?.onUpload();
      await this.updateFiles();
    };

    const ref = this.overlayService.openPopup({
      content: AddPoReportUploadComponent,
      settings: {
        header: 'Upload PO Report',
        primaryButton: {
          action,
          disabled: (instance: AddPoReportUploadComponent | null) =>
            !!instance?.isUploadButtonDisabled(),
          label: 'Save',
          pendoTag: 'upload-po-report-button',
          tooltip: (instance: AddPoReportUploadComponent | null) =>
            instance ? instance?.uploadButtonTooltip() : '',
        },
      },
    });

    const resp = await firstValueFrom(ref.afterClosed$);

    if (resp.data) {
      await this.updateFiles();
    }
  };

  async onAddVendor(node: IRowNode): Promise<void> {
    const ref = this.overlayService.openPopup<
      OrganizationDialogData,
      { vendorId: string },
      OrganizationDialogComponent
    >({
      content: OrganizationDialogComponent,
      data: {
        fetchFilesOnInit: false,
        userHasPermission: true,
      },
      settings: {
        header: 'Add New Vendor Contract',
        primaryButton: {
          action: async (instance) => {
            await instance?.onSubmit();
          },
          disabled: (instance) => !instance?.userHasPermission || !instance.hasChanges(),
          label: 'Save Vendor Contract',
          tooltip: (instance) => {
            return (
              !instance?.userHasPermission
                ? instance?.permissionMessage
                : instance?.saveBtnTooltip()
            ) as string;
          },
        },
      },
    });

    const { data } = await firstValueFrom(ref.afterClosed$);

    if (data?.vendorId) {
      this.addingVendor$.next(true);
      const { success } = await this.purchaseOrdersService.update({
        description: node.data.description,
        id: node.data.po_id,
        organization_id: data?.vendorId || '',
        po_number: node.data.po_number,
        po_status: node.data.po_status,
      });

      if (success) {
        this.overlayService.success(`Purchase order successfully updated!`);
        this.purchaseOrdersService.get().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
      }

      this.addingVendor$.next(false);
    }
  }

  onCreateChangeOrder(node: IRowNode) {
    const cOLibraryRoute = `${ROUTING_PATH.BUDGET.INDEX}/${ROUTING_PATH.BUDGET.CHANGE_ORDER.INDEX}`;

    this.router
      .navigate([cOLibraryRoute], {
        queryParams: { modalVendorId: node.data.organization_id },
      })
      .catch(console.error);
  }

  async onDownloadLine(rowNode: IRowNode) {
    if (!rowNode.data) {
      return;
    }

    const ref = this.overlayService.loading();
    const { data, errors, success } = await this.apiService.getS3ZipFile(
      this.getFilePath(rowNode.data.po_id)
    );

    if (success && data) {
      const trialName = this.mainQuery.getSelectedTrial()?.short_name;
      const { organization_name, po_number } = rowNode.data;
      const filename = `${trialName}_${organization_name}_Purchase Order ${po_number}`;

      await this.apiService.downloadZipOrFile(data, filename);

      this.overlayService.success();
    } else {
      this.overlayService.error(errors);
    }

    ref.close();
  }

  onExport = async () => {
    const trialName = this.mainQuery.getSelectedTrial()?.short_name || '';

    const { errors, success } = await firstValueFrom(
      this.eventService.processEvent$({
        entity_id: this.mainQuery.getSelectedTrial()?.id || '',
        entity_type: EntityType.TRIAL,
        payload: JSON.stringify({
          export_type: ExportType.PURCHASE_ORDERS,
          filename: `${trialName}_auxilius-PurchaseOrders_${dayjs().format('YYYY.MM.DD-HHmmss')}`,
          json_properties: {
            invoiced_through_date: this.POForm.controls.invoices_through.value
              ? dayjs(this.POForm.controls.invoices_through.value).format('YYYY-MM-DD')
              : undefined,
            po_ids: this.filteredPOIDs(),
          },
        }),
        type: EventType.GENERATE_EXPORT,
      })
    );

    if (success) {
      this.overlayService.success(
        'Export is being generated and will download when complete. You may leave the page.'
      );
    } else {
      this.overlayService.error(errors);
    }
  };

  onGridReady({ api }: GridReadyEvent) {
    this.gridAPI$.next(api);
    this.gridAPI = api;
    const pinnedRightBottom = document.querySelector('.ag-pinned-right-floating-bottom');

    if (pinnedRightBottom) {
      pinnedRightBottom.remove();
    }

    this.refreshGridFilter(this.gridData$.getValue());
  }

  async onRemoveLine(rowNode: IRowNode) {
    if (!rowNode.data) {
      return;
    }

    const resp = this.overlayService.openPopup({
      content: ConfirmationModalComponent,
      data: {
        message: `Are you sure you want to delete Purchase Order ${rowNode.data.po_number}?`,
      },
      settings: {
        header: 'Delete Purchase Order',
        primaryButton: {
          label: 'Delete',
          variant: 'negative',
        },
      },
    });

    const event = await firstValueFrom(resp.afterClosed$);

    if (event.data) {
      await this.purchaseOrdersService.deletePurchaseOrder(rowNode.data.po_id);
      this.getListBudgetTotalsByPurchaseOrder();
      this.updateInvoicePoFilterValue(rowNode.data.po_id);
    }
  }

  async onRequestChangeOrder(node: IRowNode) {
    const poId = node.data.po_id;
    const vendorID = node.data.organization_id;
    const vendorDropdownOptions =
      this.signalVendors()?.map((vendor) => {
        return { label: vendor.name, value: vendor.id } as Option;
      }) || ([] as Option[]);

    if (poId && vendorID) {
      const poDropdownOptions = this.PONumbersOptions();
      const ref = this.overlayService.openPopup<
        RequestChangeOrderData,
        { notes: string; po_option: Option; users: string[]; vendor: string },
        PoCoRequestModalComponent
      >({
        data: {
          po_id: poId,
          poDropdownItems: poDropdownOptions,
          userDropdownItems: this.requestCOUsers(),
          vendorDropdownItems: vendorDropdownOptions,
          vendorId: vendorID,
        },
        modal: PoCoRequestModalComponent,
        settings: {
          header: 'Request change order',
          primaryButton: {
            action: (instance) => {
              instance?.submitForm();
            },
            label: 'Send Request',
          },
          secondaryButton: {
            label: 'Cancel',
          },
        },
      });

      const { data } = await firstValueFrom(ref.afterClosed$);

      if (data && data.po_option && data.vendor && data.users && data.users.length) {
        const { success } = await firstValueFrom(
          this.eventService.processEvent$({
            entity_id: data.po_option.value,
            entity_type: EntityType.CHANGE_ORDER,
            payload: JSON.stringify({
              note: data.notes || '',
              po_number: data.po_option.label,
              user_emails: data.users,
              user_timestamp: dayjs().format('MMMM D, YYYY, h:mma'),
              vendor_id: data.vendor,
            }),
            type: EventType.CHANGE_ORDER_REQUEST_NOTIFICATION,
          })
        );

        if (success) {
          const response = await firstValueFrom(
            this.eventService.processEvent$({
              entity_id: data.po_option.value,
              entity_type: EntityType.CHANGE_ORDER,
              payload: JSON.stringify({
                note: data.notes || '',
                po_number: data.po_option.label,
                user_emails: data.users,
                user_timestamp: dayjs().format('MMMM D, YYYY, h:mma'),
                vendor_id: data.vendor,
              }),
              type: EventType.CHANGE_ORDER_REQUEST_CONFIRMATION_NOTIFICATION,
            })
          );

          // eslint-disable-next-line max-depth
          if (response.success) {
            this.overlayService.success('Successfully Sent!');
          } else {
            this.overlayService.error(MessagesConstants.ERROR_OCCURRED);
          }
        } else {
          this.overlayService.error(MessagesConstants.ERROR_OCCURRED);
        }
      }
    }
  }

  async onUpdateLine(rowNode: IRowNode) {
    const action = async (instance: NewPoDialogComponent | null) => {
      await instance?.onUpload();
      await this.updateFiles();
    };

    const resp = this.overlayService.openPopup<NewPoDialogData, unknown, NewPoDialogComponent>({
      content: NewPoDialogComponent,
      data: {
        isPoEditableByPivot: rowNode.data.isPoEditableByPivot,
        mode: 'update',
        po_id: rowNode.data.po_id,
      },
      settings: {
        header: 'Edit purchase order',
        primaryButton: {
          action,
          disabled: (instance) => !!instance?.isSaveButtonDisabled(),
          pendoTag: 'edit-purchase-order-button',
          tooltip: (instance) => (instance ? instance?.saveButtonTooltip() : ''),
        },
      },
    });

    const event = await firstValueFrom(resp.afterClosed$);

    if (event.data) {
      this.getListBudgetTotalsByPurchaseOrder();
    }

    await this.updateFiles();
  }

  PONumbersOptions(): Option[] {
    return this.PONumbers().map((num) => {
      return { label: num.po_number, value: num.id } as Option;
    });
  }

  refreshGridFilter(gridData: POGridData[]) {
    if (this.getSelectedCurrency() && gridData) {
      this.gridAPI?.setGridOption('pinnedBottomRowData', this.generatePinnedBottomData(gridData));
    } else {
      this.gridAPI?.setGridOption('pinnedBottomRowData', []);
    }

    this.gridAPI?.sizeColumnsToFit();
  }

  private setLastPOReportUpdated(isInitColumns?: boolean): void {
    if (isInitColumns) {
      this.lastPOReportUpdating$.next(true);
    }

    const filters = {
      // Filter by create_date to exclude legacy informational PO reports
      // The current PO report functionality was released on 2025-03-21
      // This filter ensures we only show documents created with the new functionality
      create_date: {
        dateFrom: '2025-03-21',
        filterType: 'date',
        type: 'greaterThan',
      },
      document_type_id: {
        filter: DocumentType.DOCUMENT_PO_REPORT,
        filterType: 'text',
        type: 'equals',
      },
      // ensures we don't retrieve a PO Report uploaded via Document Library
      is_metadata_editable: {
        filter: false,
        filterType: 'text',
        type: 'equals',
      },
    };

    this.gqlService
      .listDocuments$({
        end_row: 1,
        filter_model: JSON.stringify(filters),
        sort_model: [
          {
            descending: true,
            field: 'create_date',
          },
        ],
        start_row: 0,
      })
      .subscribe(({ data }) => {
        if (data?.items && data?.items.length) {
          this.lastPOReportUpdatedText.set(
            `${Utils.dateFormatter(data.items[0]?.create_date, { day: 'numeric', hour: 'numeric', minute: '2-digit', month: 'long', year: 'numeric' })} by ${this.userFormatter(data.items[0]?.created_by)}`
          );

          if (data.items.length) {
            this.isWarningsVisible$.next(true);
          }
        } else {
          this.lastPOReportUpdatedText.set(Utils.zeroHyphen);
          this.isWarningsVisible$.next(false);
        }

        if (isInitColumns) {
          this.lastPOReportUpdating$.next(false);
        }
      });
  }

  get tooltipErrors() {
    const errors: string[] = [];

    if (!this.hasPOPermission$.value) {
      errors.push(MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION);
    }

    if (this.isChangeOrdersWorkflowLocked()) {
      errors.push(MessagesConstants.LOCKED_FOR_PERIOD_CLOSE);
    }

    return errors;
  }

  private async updateFiles() {
    this.setLastPOReportUpdated();

    const files = await this.apiService.getFilesByFilters(
      this.getTrialPOPath(),
      undefined,
      EntityType.PURCHASE_ORDER,
      DocumentType.DOCUMENT_PURCHASE_ORDER
    );

    this.files$.next(files);
  }

  private updateInvoicePoFilterValue(poId: string): void {
    const purchaseOrderControl = this.invoiceService.filtersForm.controls.purchase_order;
    const invoicePoFilterValues = purchaseOrderControl.value;

    if (invoicePoFilterValues?.includes(poId)) {
      invoicePoFilterValues.splice(invoicePoFilterValues.indexOf(poId), 1);
      purchaseOrderControl.setValue(invoicePoFilterValues);
    }
  }

  private updatePoListOnBulkUpdateUploaded(): void {
    this.eventService
      .select$(EventType.PURCHASE_ORDER_BULK_UPDATE_TEMPLATE_UPLOADED, true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result.page === NotificationPage.PURCHASE_ORDERS) {
          this.purchaseOrdersService.get().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
          this.getListBudgetTotalsByPurchaseOrder();
        }
      });
  }

  private updatePoListOnBulkUploaded(): void {
    this.eventService
      .select$(EventType.PURCHASE_ORDER_BULK_TEMPLATE_UPLOADED, true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result.page === NotificationPage.PURCHASE_ORDERS) {
          this.purchaseOrdersService.get().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
          this.getListBudgetTotalsByPurchaseOrder();
        }
      });
  }

  private updatePoListOnReportUpdated(): void {
    this.eventService
      .select$(EventType.PURCHASE_ORDER_REPORT_TEMPLATE_UPLOADED, true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result.page === NotificationPage.PURCHASE_ORDERS) {
          this.purchaseOrdersService.get().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
          this.getListBudgetTotalsByPurchaseOrder();
        }
      });
  }

  get uploadPOReportTooltipErrors() {
    const errors: string[] = [];

    if (!this.hasUploadReportPermission()) {
      errors.push(MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION);
    }

    if (this.isChangeOrdersWorkflowLocked()) {
      errors.push(MessagesConstants.LOCKED_FOR_PERIOD_CLOSE);
    }

    return errors;
  }

  private userFormatter(userToFormat: string) {
    const user = this.users.get(userToFormat);

    if (user) {
      const firstName = user.given_name;
      const lastName = user.family_name;
      const isUserAuxAdmin = user.email.includes('@auxili.us');

      if (!isUserAuxAdmin || this.authQuery.isAuxAdmin()) {
        return firstName && lastName ? `${firstName} ${lastName[0]}.` : Utils.zeroHyphen;
      } else {
        return 'Auxilius Expert';
      }
    }

    return Utils.zeroHyphen;
  }

  varianceCheckbox(e: boolean) {
    this.POForm.controls.pos_with_variance.setValue(e);
  }
}
